import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

interface Props {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100, 200],
}: Props) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-2.5 text-sm">
      <div className="flex items-center gap-3 text-slate-600">
        <span>
          <strong className="text-slate-900">{from}</strong>–
          <strong className="text-slate-900">{to}</strong> de{" "}
          <strong className="text-slate-900">{total}</strong>
        </span>
        {onPageSizeChange && (
          <label className="flex items-center gap-1.5 text-xs">
            <span className="text-slate-500">Por página:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-xs"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(1)}
          disabled={!canPrev}
          className="rounded-md p-1.5 text-slate-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
          title="Primera página"
        >
          <ChevronsLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={!canPrev}
          className="rounded-md p-1.5 text-slate-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
          title="Página anterior"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="px-3 font-bold text-slate-700">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={!canNext}
          className="rounded-md p-1.5 text-slate-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
          title="Página siguiente"
        >
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          onClick={() => onPageChange(totalPages)}
          disabled={!canNext}
          className="rounded-md p-1.5 text-slate-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
          title="Última página"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </div>
  );
}

/**
 * Util: dado el array completo y los parámetros de paginación,
 * devuelve la slice visible. Mantén el estado (page/pageSize) en el componente.
 */
export function pageSlice<T>(items: T[] | undefined, page: number, pageSize: number): T[] {
  if (!items) return [];
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}
